import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import './AdminLogin.css';

// Check if we're in test/local auth mode
const isTestEnvironment = process.env.REACT_APP_AUTH_MODE === 'local' ||
                          process.env.REACT_APP_ENVIRONMENT === 'test';

const AdminLogin = () => {
    const auth = useAuth();
    const { loginWithRedirect, isAuthenticated, isLoading, user } = auth;
    // loginWithCredentials is only available in LocalAuthProvider
    const loginWithCredentials = auth.loginWithCredentials;
    const navigate = useNavigate();

    // Local auth form state
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        // If already authenticated, redirect to dashboard
        if (isAuthenticated && user) {
            navigate('/admin/dashboard');
        }
    }, [isAuthenticated, user, navigate]);

    const handleGoogleLogin = () => {
        loginWithRedirect({
            authorizationParams: {
                connection: 'google-oauth2',
                prompt: 'select_account'
            }
        });
    };

    const handleLocalLogin = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {
            const result = await loginWithCredentials(username, password);
            if (result.success) {
                navigate('/admin/dashboard');
            } else {
                setError(result.error || 'Invalid credentials');
            }
        } catch (err) {
            setError('Login failed. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="admin-login-page">
                <div className="admin-login-container">
                    <div className="admin-login-header">
                        <h1>Loading...</h1>
                        <p>Please wait</p>
                    </div>
                </div>
            </div>
        );
    }

    // Test environment: show username/password form
    if (isTestEnvironment) {
        return (
            <div className="admin-login-page">
                <div className="admin-login-container">
                    <div className="admin-login-header">
                        <h1>PCIC Admin</h1>
                        <p>Content Management System</p>
                        <span className="test-badge">TEST ENVIRONMENT</span>
                    </div>
                    <form className="admin-login-form" onSubmit={handleLocalLogin}>
                        <div className="admin-login-info">
                            <p>Sign in with your test credentials</p>
                        </div>
                        {error && (
                            <div className="admin-login-error">
                                {error}
                            </div>
                        )}
                        <div className="admin-form-group">
                            <label htmlFor="username">Username</label>
                            <input
                                type="text"
                                id="username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="Enter username"
                                required
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className="admin-form-group">
                            <label htmlFor="password">Password</label>
                            <input
                                type="password"
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter password"
                                required
                                disabled={isSubmitting}
                            />
                        </div>
                        <button
                            type="submit"
                            className="admin-login-btn"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Signing in...' : 'Sign In'}
                        </button>
                        <p className="admin-login-note">
                            This is a test environment.
                            <br />
                            Use provided test credentials.
                        </p>
                    </form>
                </div>
            </div>
        );
    }

    // Production: show Google login
    return (
        <div className="admin-login-page">
            <div className="admin-login-container">
                <div className="admin-login-header">
                    <h1>PCIC Admin</h1>
                    <p>Content Management System</p>
                </div>
                <div className="admin-login-form">
                    <div className="admin-login-info">
                        <p>Sign in with your @pcic.org.au Google account</p>
                    </div>
                    <button
                        onClick={handleGoogleLogin}
                        className="admin-login-btn google-btn"
                    >
                        <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Sign in with Google
                    </button>
                    <p className="admin-login-note">
                        Only @pcic.org.au email addresses are authorized.
                        <br />
                        New users require admin approval.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AdminLogin;
